/**
 * Tracks scroll activity so scrollbars can be styled as "visible only while
 * scrolling". CSS has no way to observe scrolling, so this listener flags it on
 * the root element and `index.css` keys the scrollbar colors off that flag.
 */

/** How long the scrollbar stays visible after the last scroll event. */
const SCROLL_IDLE_DELAY_MS = 700;

/** Attribute set on `<html>` while the user is scrolling. */
const SCROLLING_ATTRIBUTE = "data-scrolling";

/**
 * Starts flagging scroll activity on the document root.
 *
 * Listens in the capture phase because `scroll` events from nested scroll
 * containers do not bubble, so a single document-level listener covers every
 * scrollable region in the app.
 *
 * @returns Cleanup function that removes the listener and clears the flag
 */
export const initScrollbarVisibility = (): (() => void) => {
  const root = document.documentElement;
  let idleTimeoutId: number | undefined;

  /**
   * Marks the root as scrolling and schedules the flag to clear once the user
   * stops.
   */
  const handleScroll = () => {
    root.setAttribute(SCROLLING_ATTRIBUTE, "true");

    window.clearTimeout(idleTimeoutId);
    idleTimeoutId = window.setTimeout(() => {
      root.removeAttribute(SCROLLING_ATTRIBUTE);
    }, SCROLL_IDLE_DELAY_MS);
  };

  document.addEventListener("scroll", handleScroll, {
    capture: true,
    passive: true,
  });

  return () => {
    document.removeEventListener("scroll", handleScroll, { capture: true });
    window.clearTimeout(idleTimeoutId);
    root.removeAttribute(SCROLLING_ATTRIBUTE);
  };
};
